import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2, Search, X, Move } from "lucide-react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

/**
 * AddressSearch — search-as-you-type address autocomplete backed by
 * OpenStreetMap Nominatim (free, keyless). Below the input we render an
 * INTERACTIVE Leaflet map with a **draggable pin**: dragging the pin runs
 * reverse-geocoding via Nominatim and auto-fills every address field.
 *
 * Emits: { address, lat, lng, street, number, postcode, city, country }
 */

// react-leaflet doesn't auto-load marker icons via bundlers. Point to the CDN.
const DEFAULT_ICON = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

// Small child component so we can imperatively pan the map when the user picks
// a new address from the search dropdown.
function MapPanTo({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) {
      map.setView([lat, lng], Math.max(map.getZoom(), 14), { animate: true });
    }
  }, [lat, lng, map]);
  return null;
}

async function nominatimSearch(query, signal) {
  const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=6&q=${encodeURIComponent(query)}`;
  const r = await fetch(url, {
    signal,
    headers: { "Accept-Language": navigator.language || "en" },
  });
  return r.json();
}

async function nominatimReverse(lat, lng, signal) {
  const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&addressdetails=1&lat=${lat}&lon=${lng}`;
  const r = await fetch(url, {
    signal,
    headers: { "Accept-Language": navigator.language || "en" },
  });
  return r.json();
}

function toPayload(r, latOverride, lngOverride) {
  const a = r.address || {};
  return {
    address: r.display_name || "",
    lat: latOverride ?? parseFloat(r.lat),
    lng: lngOverride ?? parseFloat(r.lon),
    street: a.road || a.pedestrian || a.footway || a.residential || "",
    number: a.house_number || "",
    postcode: a.postcode || "",
    city: a.city || a.town || a.village || a.county || "",
    country: a.country || "",
  };
}

export default function AddressSearch({
  value = "",
  onChange,
  placeholder = "Search an address…",
  testid = "address-search",
  showMap = true,
}) {
  const [query, setQuery] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reversing, setReversing] = useState(false);
  const [picked, setPicked] = useState(value ? { address: value } : null);
  const boxRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    setQuery(value || "");
    if (value) setPicked((p) => (p && p.address === value ? p : { address: value }));
  }, [value]);

  // Debounced text search
  useEffect(() => {
    if (!query || query.trim().length < 3 || (picked && picked.address === query)) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        setBusy(true);
        if (abortRef.current) abortRef.current.abort();
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        const data = await nominatimSearch(query, ctrl.signal);
        setResults(Array.isArray(data) ? data : []);
        setOpen(true);
      } catch { /* aborted / offline */ }
      finally { setBusy(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [query, picked]);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const pick = (r) => {
    const payload = toPayload(r);
    setPicked(payload);
    setQuery(payload.address);
    setOpen(false);
    setResults([]);
    if (onChange) onChange(payload);
  };

  const clear = () => {
    setPicked(null);
    setQuery("");
    setResults([]);
    if (onChange) onChange({ address: "", lat: null, lng: null, street: "", number: "", postcode: "", city: "", country: "" });
  };

  // Reverse-geocode whenever the pin is dragged to a new location.
  const handleMarkerDragEnd = async (e) => {
    const { lat, lng } = e.target.getLatLng();
    setReversing(true);
    try {
      const data = await nominatimReverse(lat, lng);
      const payload = toPayload(data, lat, lng);
      setPicked(payload);
      setQuery(payload.address);
      if (onChange) onChange(payload);
    } catch { /* ignore */ }
    finally { setReversing(false); }
  };

  const canRenderMap = showMap && picked?.lat != null && picked?.lng != null;

  return (
    <div className="relative" ref={boxRef} data-testid={testid}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C8C8C]"/>
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); if (picked) setPicked(null); }}
          onFocus={() => results.length && setOpen(true)}
          placeholder={placeholder}
          className="input pl-9 pr-9"
          data-testid={`${testid}-input`}
          autoComplete="off"
        />
        {(busy || query) && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8C8C8C]">
            {busy
              ? <Loader2 size={14} className="animate-spin"/>
              : query
                ? <button type="button" onClick={clear} aria-label="Clear address" data-testid={`${testid}-clear`}><X size={14}/></button>
                : null}
          </div>
        )}
      </div>

      {open && results.length > 0 && (
        <ul className="absolute left-0 right-0 top-full mt-1 max-h-72 overflow-auto bg-white rounded-2xl shadow-lg border border-[#E8E8E8] z-30"
            data-testid={`${testid}-suggestions`}>
          {results.map((r) => (
            <li key={r.place_id}>
              <button type="button" onClick={() => pick(r)}
                className="w-full text-left px-3 py-2.5 hover:bg-[#F5F5F5] flex items-start gap-2 text-sm border-b border-[#F2F2F2] last:border-0"
                data-testid={`${testid}-result-${r.place_id}`}>
                <MapPin size={13} className="text-[#0057FF] mt-0.5 flex-shrink-0"/>
                <span className="truncate">{r.display_name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {canRenderMap && (
        <div className="mt-2 rounded-2xl overflow-hidden border border-[#E8E8E8] relative" data-testid={`${testid}-map`}>
          <MapContainer
            center={[picked.lat, picked.lng]}
            zoom={15}
            style={{ height: 240, width: "100%" }}
            scrollWheelZoom={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapPanTo lat={picked.lat} lng={picked.lng}/>
            <Marker
              position={[picked.lat, picked.lng]}
              draggable
              eventHandlers={{ dragend: handleMarkerDragEnd }}
              icon={DEFAULT_ICON}
            />
          </MapContainer>
          <div className="absolute top-2 left-2 z-[400] bg-white/95 backdrop-blur px-2.5 py-1 rounded-full text-[11px] text-[#4A4A4A] inline-flex items-center gap-1 shadow-sm pointer-events-none"
               data-testid={`${testid}-map-hint`}>
            <Move size={11} className="text-[#0057FF]"/>
            {reversing ? "Updating address…" : "Drag the pin to refine the location"}
          </div>
        </div>
      )}
    </div>
  );
}
